import { z } from 'zod'
import { generatedAssetSchema } from './ai-studio-asset'
import {
  EVENT_DRAFT_CREDIT_COST,
  type EventFlowState,
  eventFlowStateSchema,
} from './ai-studio-event'
import type { FormatPreset } from './ai-studio-format'
import { REFINE_CREDIT_COST } from './ai-studio-refine'
import { EVENT_TRANSLATION_CREDIT_COST } from './event-description'

/** Four cover variants at medium quality, the chosen one finished in high quality. */
export const EVENT_COVER_CREDIT_COST = 300
/** Extending a photo's background with AI so it fills a format it does not fit. */
export const PHOTO_EXTEND_CREDIT_COST = 50
/** One more channel format of a cover made in the chat, beyond the ones the package includes. */
const EVENT_FORMAT_CREDIT_COST = 50
/** Text, cover, English version, feature check and two channel formats together, charged once. */
export const EVENT_PACKAGE_CREDIT_COST = 600
/** Channel formats beyond the Pulsify cover that the package includes. */
export const EVENT_PACKAGE_FREE_FORMATS = 2

/** The price of every step of the AI event flow, in credits. */
export const EVENT_FLOW_PRICES = {
  text: EVENT_DRAFT_CREDIT_COST,
  cover: EVENT_COVER_CREDIT_COST,
  format: EVENT_FORMAT_CREDIT_COST,
  refine: REFINE_CREDIT_COST,
  translation: EVENT_TRANSLATION_CREDIT_COST,
  ownPhoto: 0,
  extendBackground: PHOTO_EXTEND_CREDIT_COST,
  package: EVENT_PACKAGE_CREDIT_COST,
} as const

/** What an organizer has used in one run of the flow. */
export interface EventFlowUsage {
  package: boolean
  text: boolean
  cover: boolean
  translation: boolean
  extraFormats: number
  refines: number
  extensions: number
}

/** Credits for recomposing `count` channel formats when `free` of them are still included. */
export function eventFlowFormatsPrice(count: number, free = 0): number {
  return Math.max(0, count - Math.max(0, free)) * EVENT_FLOW_PRICES.format
}

/** Credits one run of the flow costs: the package replaces text, cover, English and two formats. */
export function eventFlowCost(usage: EventFlowUsage): number {
  const core = usage.package
    ? EVENT_PACKAGE_CREDIT_COST
    : (usage.text ? EVENT_FLOW_PRICES.text : 0) +
      (usage.cover ? EVENT_FLOW_PRICES.cover : 0) +
      (usage.translation ? EVENT_FLOW_PRICES.translation : 0)
  return (
    core +
    eventFlowFormatsPrice(usage.extraFormats, usage.package ? EVENT_PACKAGE_FREE_FORMATS : 0) +
    usage.refines * EVENT_FLOW_PRICES.refine +
    usage.extensions * EVENT_FLOW_PRICES.extendBackground
  )
}

export const eventFlowStepValues = ['text', 'cover', 'translation'] as const
export type EventFlowStep = (typeof eventFlowStepValues)[number]

/** What a step costs right now: nothing while the package still covers it. */
export function eventFlowStepPrice(step: EventFlowStep, state: EventFlowState | null): number {
  if (step === 'cover' && state?.free.cover) return 0
  if (step === 'translation' && state?.free.translation) return 0
  return EVENT_FLOW_PRICES[step]
}

export const eventChannelValues = [
  'pulsify-cover',
  'instagram-post',
  'instagram-story',
  'whatsapp-status',
  'facebook',
  'flyer-a6',
  'email-header',
] as const
export type EventChannel = (typeof eventChannelValues)[number]
export const eventChannelSchema = z.enum(eventChannelValues)

/** The format each channel is rendered in. */
export const EVENT_CHANNEL_PRESET: Record<EventChannel, FormatPreset> = {
  'pulsify-cover': 'pulsify-cover',
  'instagram-post': 'instagram-portrait',
  'instagram-story': 'instagram-story',
  'whatsapp-status': 'whatsapp-status',
  facebook: 'facebook-wide',
  'flyer-a6': 'flyer-a6',
  'email-header': 'email-header',
}

/** Every channel except the Pulsify cover, which is always made. */
export const EXTRA_EVENT_CHANNELS = eventChannelValues.filter(
  (channel) => channel !== 'pulsify-cover',
)
export const extraEventChannelSchema = eventChannelSchema.exclude(['pulsify-cover'])
export type ExtraEventChannel = z.infer<typeof extraEventChannelSchema>

export const coverStyleValues = ['grafik', 'poster', '3d', 'minimal', 'typo', 'retro'] as const
export type CoverStyle = (typeof coverStyleValues)[number]
export const coverStyleSchema = z.enum(coverStyleValues)

export const logoPlacementValues = ['top-right', 'bottom', 'none'] as const
export type LogoPlacement = (typeof logoPlacementValues)[number]
export const logoPlacementSchema = z.enum(logoPlacementValues)

export const coverTextModeValues = ['title-date', 'title', 'none'] as const
export type CoverTextMode = (typeof coverTextModeValues)[number]
export const coverTextModeSchema = z.enum(coverTextModeValues)

export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/)
export const BRAND_PALETTE_MAX = 5
export const brandPaletteSchema = z.array(hexColorSchema).max(BRAND_PALETTE_MAX)

/** The defaults "Einfach machen" uses instead of asking. */
export const COVER_DEFAULTS = {
  style: 'grafik',
  logo: 'top-right',
  text: 'title-date',
} as const satisfies { style: CoverStyle; logo: LogoPlacement; text: CoverTextMode }

export const COVER_VARIANT_COUNT = 4

export const coverVariantsRequestSchema = z.object({
  flowId: z.string().uuid(),
  style: coverStyleSchema,
  logo: logoPlacementSchema,
  text: coverTextModeSchema,
  title: z.string().trim().max(120).default(''),
  dateLabel: z.string().trim().max(80).default(''),
  about: z.string().trim().max(800).default(''),
  note: z.string().trim().max(300).default(''),
  colors: brandPaletteSchema.default([]),
})
export type CoverVariantsRequest = z.infer<typeof coverVariantsRequestSchema>

export const coverImageSchema = z.object({
  imageBase64: z.string(),
  mimeType: z.string(),
})
export type CoverImage = z.infer<typeof coverImageSchema>

/**
 * The text fields sent with the organizer's own photo, as multipart form fields, to have the AI
 * set the event's title into it in four takes; the answer is a `CoverVariantsResponse`.
 */
export const photoCoverFieldsSchema = z.object({
  flowId: z.string().uuid(),
  text: z.enum(['title-date', 'title']),
  title: z.string().trim().min(1).max(120),
  dateLabel: z.string().trim().max(80).default(''),
  note: z.string().trim().max(300).default(''),
})
export type PhotoCoverFields = z.infer<typeof photoCoverFieldsSchema>

/** Where a cover's picture came from; a photo keeps its people through every later edit. */
export const coverSourceValues = ['graphic', 'photo'] as const
export type CoverSource = (typeof coverSourceValues)[number]
export const coverSourceSchema = z.enum(coverSourceValues)

export const coverVariantsResponseSchema = z.object({
  variants: z.array(coverImageSchema),
  flow: eventFlowStateSchema,
})
export type CoverVariantsResponse = z.infer<typeof coverVariantsResponseSchema>

/** Quick refine instructions offered as chips; anything else is typed as one sentence. */
export const COVER_REFINE_MAX = 300

export const coverRefineResponseSchema = z.object({ image: coverImageSchema })
export type CoverRefineResponse = z.infer<typeof coverRefineResponseSchema>

export const coverFinalizeResponseSchema = z.object({
  /** The finished design without the logo, the source for further formats. */
  master: coverImageSchema,
  cover: generatedAssetSchema,
  flow: eventFlowStateSchema,
})
export type CoverFinalizeResponse = z.infer<typeof coverFinalizeResponseSchema>

export const channelAssetSchema = z.object({
  channel: eventChannelSchema,
  asset: generatedAssetSchema,
})
export type ChannelAsset = z.infer<typeof channelAssetSchema>

export const channelAssetsResponseSchema = z.object({ assets: z.array(channelAssetSchema) })
export type ChannelAssetsResponse = z.infer<typeof channelAssetsResponseSchema>

export const coverFormatsResponseSchema = channelAssetsResponseSchema.extend({
  /** The flow after the formats were made, when they were made in one. */
  flow: eventFlowStateSchema.nullable(),
})
export type CoverFormatsResponse = z.infer<typeof coverFormatsResponseSchema>

export const photoFontValues = ['inter', 'montserrat', 'oswald', 'playfair', 'bebas'] as const
export type PhotoFont = (typeof photoFontValues)[number]

export const photoTextEffectValues = ['none', 'shadow', 'gradient'] as const
export type PhotoTextEffect = (typeof photoTextEffectValues)[number]

/** Text and logo on an own photo; positions and size are relative to each format. */
export const photoLayoutSchema = z.object({
  title: z.string().trim().max(120).default(''),
  subtitle: z.string().trim().max(120).default(''),
  font: z.enum(photoFontValues).default('montserrat'),
  /** Title height as a share of the format's shorter side. */
  size: z.number().min(0.04).max(0.2).default(0.09),
  /** Centre of the text block, 0 to 1 across and down. */
  x: z.number().min(0).max(1).default(0.5),
  y: z.number().min(0).max(1).default(0.8),
  color: hexColorSchema.default('#ffffff'),
  effect: z.enum(photoTextEffectValues).default('shadow'),
  logo: logoPlacementSchema.default('top-right'),
})
export type PhotoLayout = z.infer<typeof photoLayoutSchema>

export const photoComposeRequestSchema = z.object({
  layout: photoLayoutSchema,
  channels: z.array(eventChannelSchema).min(1).max(eventChannelValues.length),
  /** Channels whose background AI extends instead of cropping the photo. */
  extend: z.array(eventChannelSchema).max(eventChannelValues.length).default([]),
})
export type PhotoComposeRequest = z.infer<typeof photoComposeRequestSchema>

/** What an own photo in these channels costs: only AI-extended backgrounds are paid. */
export function photoComposeCost(request: Pick<PhotoComposeRequest, 'channels' | 'extend'>) {
  const extended = new Set(request.extend.filter((channel) => request.channels.includes(channel)))
  return extended.size * PHOTO_EXTEND_CREDIT_COST
}

export const brandProfileResponseSchema = z.object({
  profileId: z.string().uuid().nullable(),
  logoUrl: z.string().nullable(),
  colors: brandPaletteSchema,
})
export type BrandProfileResponse = z.infer<typeof brandProfileResponseSchema>

export const updateBrandColorsRequestSchema = z.object({ colors: brandPaletteSchema })
export type UpdateBrandColorsRequest = z.infer<typeof updateBrandColorsRequestSchema>

export const eventFeatureValues = ['liveRoom', 'signup', 'tickets'] as const
export type EventFeature = (typeof eventFeatureValues)[number]
export type EventFeatureChoice = Record<EventFeature, boolean>

/** Which event features to switch on, from what the draft found. */
export function suggestEventFeatures(draft: {
  ticketUrl: string | null
  signupRequired?: boolean
}): EventFeatureChoice {
  return {
    liveRoom: true,
    signup: draft.signupRequired === true,
    tickets: Boolean(draft.ticketUrl?.trim()),
  }
}
